import { useMutation, useQuery } from '@tanstack/react-query'
import debounce from 'lodash/debounce'
import React from 'react'

import AsyncSelect from '@/components/select/AsyncSelect'
import { useCurrentGroup } from '@/hooks/useCurrentGroup'
import { QUERY_KEYS } from '@/lib/queryKeys'
import companyUserService from '@/lib/services/companyUserService'
import { SelectOption } from '@/types/misc'
import { CompanyUserItem } from '@/types/responses/company-users'

type AsyncSelectProps = React.ComponentProps<typeof AsyncSelect>

type Props = Pick<
  AsyncSelectProps,
  'defaultMenuIsOpen' | 'placeholder' | 'noOptionsMessage'
> & {
  excludedUserIds?: number[]
  excludeNonAdmins?: boolean
  isDisabled?: boolean
  isClearable?: boolean
  onSelect: (companyUser: CompanyUserItem | null) => void
  selected?: CompanyUserItem | null // If undefined, the selection will be managed by react-select
}

const SelectTeamMember: React.FC<Props> = ({
  defaultMenuIsOpen,
  excludedUserIds,
  excludeNonAdmins,
  isDisabled,
  isClearable,
  noOptionsMessage,
  onSelect,
  placeholder,
  selected,
}) => {
  const currentGroup = useCurrentGroup()

  const companyUsersQuery = useQuery({
    queryKey: QUERY_KEYS.companyUsers(),
    queryFn: () => companyUserService.getCompanyUsers(),
    refetchOnWindowFocus: false,
  })

  const companyUsers = companyUsersQuery.data?.['hydra:member'] || []
  const filteredCompanyUsers = companyUsers.filter(
    (companyUser) =>
      !excludedUserIds?.includes(companyUser.user.id) &&
      (!excludeNonAdmins || companyUser.isCompanyAdmin),
  )

  const options: SelectOption[] = filteredCompanyUsers.map((companyUser) =>
    makeOption(companyUser),
  )

  const searchCompanyUsersMutation = useMutation({
    mutationFn: (companyUserName: string) =>
      companyUserService.searchCompanyUsers(companyUserName),
  })

  function loadOptions(
    name: string,
    callback: (options: SelectOption[]) => void,
  ) {
    searchCompanyUsersMutation.mutateAsync(name).then((response) => {
      const options = response['hydra:member']
        .filter(
          (companyUser) =>
            !excludedUserIds?.includes(companyUser.user.id) &&
            (!excludeNonAdmins || companyUser.isCompanyAdmin),
        )
        .map((companyUser) => makeOption(companyUser))

      return callback(options)
    })
  }

  const debouncedLoadOptions = debounce(loadOptions, 300)

  async function getCompanyUserByOption(
    option: SelectOption | null,
  ): Promise<CompanyUserItem | null> {
    if (!option) {
      return null
    }

    return await getCompanyUserMutation.mutateAsync(+option.value)
  }

  const getCompanyUserMutation = useMutation({
    mutationFn: (companyUserId: number) =>
      companyUserService.getCompanyUser(companyUserId),
  })

  const isLoading =
    searchCompanyUsersMutation.isPending || getCompanyUserMutation.isPending

  return (
    <AsyncSelect
      value={getValue(selected)}
      isLoading={isLoading}
      isDisabled={isDisabled}
      isClearable={isClearable}
      cacheOptions
      defaultOptions
      options={options}
      defaultMenuIsOpen={defaultMenuIsOpen}
      loadOptions={debouncedLoadOptions}
      noOptionsMessage={noOptionsMessage}
      onChange={async (option) => {
        const companyUser = await getCompanyUserByOption(option)
        onSelect(companyUser)
      }}
      placeholder={
        getCompanyUserMutation.isPending
          ? 'Loading...'
          : placeholder || (excludeNonAdmins ? 'Team Admin' : 'Team Member')
      }
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 2000 }), // higher than modal z-index
      }}
    />
  )

  function getValue(selected: Props['selected']) {
    if (typeof selected === 'undefined') {
      return undefined
    }

    return selected ? makeOption(selected) : null
  }

  function makeOption(companyUser: {
    id: number
    user: { fullName: string }
    company?: { presentationName: string }
  }): SelectOption {
    let name = companyUser.user.fullName

    if (currentGroup && companyUser.company) {
      name += ` (${companyUser.company.presentationName})`
    }

    return {
      label: name,
      value: companyUser.id.toString(),
    }
  }
}

export default SelectTeamMember
